import { Controller } from "@hotwired/stimulus"

// Manages layout preference using localStorage and syncs with cookies
export default class extends Controller {
  static values = {
    cabinet: String  // Current cabinet type (teachers, coaches, kindreds, managers)
  }

  static cabinets = ["teachers", "coaches", "kindreds", "managers"]

  connect() {
    // On page load, restore layout from localStorage and sync to cookie
    this.restoreLayout()

    // Check if we're on a specific cabinet page
    this.detectAndSaveCabinet()
  }

  // Detect cabinet from any element with data-layout-cabinet-value
  detectAndSaveCabinet() {
    // First check if controller has cabinet value
    if (this.hasCabinetValue && this.cabinetValue) {
      this.saveLayout(this.cabinetValue)
      return
    }

    // Look for any element with cabinet data attribute
    const cabinetElement = document.querySelector('[data-layout-cabinet-value]')
    if (cabinetElement) {
      const cabinet = cabinetElement.dataset.layoutCabinetValue
      this.saveLayout(cabinet)
    }
  }

  // Save layout preference to localStorage and cookie
  saveLayout(cabinetType) {
    if (!cabinetType) return

    // Save to localStorage
    localStorage.setItem('current_cabinet', cabinetType)

    // Sync to cookie for server-side access
    this.setCookie('current_cabinet', cabinetType, 365)
  }

  // Restore layout from localStorage and sync to cookie
  restoreLayout() {
    const savedCabinet = localStorage.getItem('current_cabinet')

    if (savedCabinet && this.isValidCabinet(savedCabinet)) {
      // Sync to cookie so server can access it
      this.setCookie('current_cabinet', savedCabinet, 365)
      return
    }

    const cookieCabinet = this.getCookie('current_cabinet')
    if (cookieCabinet && this.isValidCabinet(cookieCabinet)) {
      localStorage.setItem('current_cabinet', cookieCabinet)
      return
    }

    if (savedCabinet || cookieCabinet) {
      this.clearCabinetStorage()
      this.logCabinetIssue("Invalid cabinet value detected; cleared storage.")
      return
    }

    this.logCabinetIssue("No current_cabinet found in cookies or localStorage.")
  }

  // Switch cabinet when user clicks a cabinet link
  switchCabinet(event) {
    const cabinetType = event.currentTarget.dataset.cabinet

    if (cabinetType) {
      this.saveLayout(cabinetType)
    }
  }

  // Helper to set cookie
  setCookie(name, value, days) {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    const expires = "expires=" + date.toUTCString()
    const secure = window.location.protocol === "https:" ? ";secure" : ""
    document.cookie = name + "=" + value + ";" + expires + ";path=/;samesite=lax" + secure
  }

  getCookie(name) {
    const cookies = document.cookie ? document.cookie.split("; ") : []
    const prefix = name + "="
    for (let i = 0; i < cookies.length; i += 1) {
      if (cookies[i].startsWith(prefix)) {
        return cookies[i].slice(prefix.length)
      }
    }
    return null
  }

  isValidCabinet(value) {
    return this.constructor.cabinets.includes(value)
  }

  clearCabinetStorage() {
    localStorage.removeItem('current_cabinet')
    this.setCookie('current_cabinet', '', -1)
  }

  logCabinetIssue(message) {
    if (window?.console?.warn) {
      console.warn(`[layout_controller] ${message}`)
    }
  }
}
